import ls from './localStorage'
import { env } from './config'
import { request, mountUrl, getLocalStorageWithExpiry, setLocalStorageWithExpiry, getLocaleParam } from './utils'
import { getPermission } from '~/geoHelper'

import Cookies from '~/cookie'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { getCountryISO3 } from './countries'

const isAnonymous = Cookies.get('session-mode')?.toLowerCase().includes('anonimo')
const FALLBACK_COUNTRY_CODE = 'BRA'

function makeHash(prefix) {
  return `${prefix}-${Date.now()}-${Math.random() * 1000}`
}

function createLogger() {
  const fingerprint = ls.getItem('finger') || ls.setItem('finger', makeHash('finger'))
  let sessionId
  let position

  return {
    setPosition(newValue) {
      position = newValue
      sessionId = makeHash('session')
      // this.emit('sessionStart', { referrer: document.referrer, href: window.location.href })
    },

    emit(action, data) {
      data.brand = data.brand || window.appData.cnpj

      request(`${env.PREFIX}/logger`, {
        method: 'POST',
        body: { position, action, data, sessionId, fingerprint },
      })
    },

    getSessionId() {
      return 'teste-deletar'
      // return sessionId
    },
  }
}

export const logger = createLogger()

function filterDateLimit(item) {
  if (!item.limitDate) {
    return true
  }
  const [year, month, day] = item.limitDate.split('-').map((n) => parseInt(n))
  const limitDate = new Date(year, month - 1, day - 1, 23, 59)
  return limitDate > new Date()
}

function orderFeatured(products) {
  if (!products) return null
  if (!window.appData.featured) return products

  let listFeatured = []

  window.appData.featured
    .filter(filterDateLimit)
    .slice(0, 5)
    .forEach((item) => {
      const product = products.find((p) => p.gtin === item.gtin)

      if (product && listFeatured.length < 5) {
        listFeatured.push({ ...product, featured: item })
      }
    })

  listFeatured = listFeatured.sort((a, b) => (a.featured.order < b.featured.order ? -1 : 1))

  return listFeatured.concat(products.filter((p) => !listFeatured.find((f) => f.gtin === p.gtin)))
}

// factory
function createApi() {
  return {
    async getProducts(cnpj, userLocale = 'pt-BR') {
      const items = await request(`${env.PREFIX}/products2`, { params: { cnpj, userLocale } })
      return orderFeatured(items)
    },

    async getStores({ gtins, lng, ...params }) {
      const geoLocation = await getPermission()
      const sessionMode = Cookies.get('session-mode')
      const response = await request(`${env.PREFIX}/search-stores2/${gtins}`, {
        params: { ...params, lon: lng, geoLocation, sessionMode },
      })

      if (response.stores.length === 0) {
        throw new Error('empty_stores')
      }

      return response
    },

    // busca de produtos por termos (name)
    async search({ lat = 0, lng = 0, cnpj: brand = '', partner = '', name, gtin, locale = '' }) {
      let userLocale = locale

      if (!userLocale) {
        const isLanguageBrazilianPortuguese = window.appData.language && window.appData.language === 'pt-BR'

        if (!isLanguageBrazilianPortuguese) {
          const regionLocaleFromIP = await this.getLocationFromIP('gfnd.region:location')
          userLocale = regionLocaleFromIP?.language ?? ''
        }
      }

      const sessionMode = Cookies.get('session-mode')

      const params = {
        name: name ? encodeURI(name) : undefined,
        lat,
        lng,
        partner,
        brand,
        gtin,
        sessionMode,
        userLocale: userLocale || 'pt-BR',
      }

      const products = await request(`${env.PREFIX}/search-products2`, { params })

      return products
    },

    clickTrack({ event, ...data }) {
      if (isAnonymous) {
        return
      }
      return logger.emit(event, data)
    },

    getStoreDetails(cnpj) {
      return request(`${env.PREFIX}/store/${cnpj}/details`)
    },

    getStoreProducts(cnpj) {
      return request(`${env.PREFIX}/store/${cnpj}/products`)
    },

    retailMessage(body) {
      return request(`${env.PREFIX}/retailermessage`, {
        method: 'POST',
        body,
      })
    },

    getLabel(
      {
        address: { label, houseNumber, street, district, city, state, county, postalCode },
        matchLevel,
        localityType,
        resultType,
      },
      getLiteral,
    ) {
      if (label) return label

      const type = matchLevel || localityType || resultType
      const province = state || county

      if (type === 'houseNumber')
        return `${street}, ${houseNumber}, ${district ? ` ${district} - ` : ''}${city} - ${province}`
      if (type === 'street') return `${street}, ${district ? ` ${district} - ` : ''}${city} - ${province}`
      if (type === 'district') return `${getLiteral('1284')}: ${district}, ${city} - ${province}`
      if (type === 'city') return `${getLiteral('1231')}: ${city} - ${province}`
      if (type === 'state' || type === 'county') return `${getLiteral('1443')}: ${province}`
      if (type === 'postalCode') return `${getLiteral('1444')}: ${postalCode}, ${city} - ${province}`
    },

    fetchGeoCode({
      address: { country, state, street = '', houseNumber = '', city = '', postalCode = '' },
      id,
      label,
      location = null,
    }) {
      let params, apiServiceName

      const { lat, lng } = location || { lat: 0, lng: 0 }

      if (id) {
        params = {
          apiKey: env.HERE_REST_APIKEY,
          id: encodeURI(id),
        }
        apiServiceName = 'lookup'
      } else {
        const s = (key, value) => (value ? `${key}=${value};` : '')

        params = {
          ...(lat && lng && { at: `${lat}%2C${lng}` }),
          apiKey: env.HERE_REST_APIKEY,
          show: 'countryInfo',
        }

        const qualifiedQuery = {
          country,
          state,
          street,
          houseNumber,
          city,
          postalCode,
        }

        const buildQueryString = Object.entries(qualifiedQuery)
          .map(([key, value]) => s(key, value))
          .join('')
          .slice(0, -1)

        label ? (params.q = encodeURI(label)) : (params.qq = encodeURI(buildQueryString))
        apiServiceName = 'geocode'
      }

      return request(mountUrl(`https://${apiServiceName}.search.hereapi.com/v1/${apiServiceName}`, params), {
        contentType: false,
      })
    },

    fetchGeoCodeGoogle(address) {
      return request(mountUrl(`${env.PREFIX}/geocode`, { address }), {
        contentType: false,
      })
    },

    fetchReverseGeocode({ lat, lng }) {
      const params = {
        at: `${lat}%2C${lng}`,
        lang: 'pt-BR',
        limit: 1,
        apiKey: env.HERE_REST_APIKEY,
      }
      return request(mountUrl('https://revgeocode.search.hereapi.com/v1/revgeocode', params), {
        contentType: false,
      })
    },

    async getLocationFromIP(key) {
      const cachedLocation = getLocalStorageWithExpiry(key)

      if (cachedLocation) return cachedLocation

      try {
        const res = await fetch('https://ipapi.co/json/')
        const data = await res.json()

        const locationFromIP = {
          countryCode: data.country_code_iso3 ?? 'BRA',
          language: data.languages?.split(',')[0] ?? 'pt-BR',
        }

        setLocalStorageWithExpiry(key, locationFromIP, 1000 * 60 * 60 * 24) // '24 hours

        return { ...locationFromIP }
      } catch {
        return null
      }
    },

    async searchLocation(input, config, position, location) {
      let regionLocationByIP = null
      let isLanguageBrazilianPortuguese = ''
      let language = '',
        country = ''

      const userLocaleQueryParam = getLocaleParam(location.search)
      if (userLocaleQueryParam) {
        const { locale, countryISO3 } = userLocaleQueryParam

        if (locale) language = locale
        if (countryISO3) country = countryISO3
      } else {
        isLanguageBrazilianPortuguese = config.language && config.language === 'pt-BR'

        if (!isLanguageBrazilianPortuguese) {
          regionLocationByIP = await this.getLocationFromIP('gfnd.region:location')
        }
      }

      const userCountryCode =
        country || (regionLocationByIP?.countryCode ?? (position?.user?.countryCode || position?.user?.country))
      const selectedCountryCode = userCountryCode ?? config?.country ?? FALLBACK_COUNTRY_CODE

      const selectedLanguage =
        language || (regionLocationByIP?.language ?? navigator.language ?? config.language ?? 'pt-BR')

      const params = {
        q: encodeURIComponent(input),
        in: isLanguageBrazilianPortuguese
          ? `countryCode:${FALLBACK_COUNTRY_CODE}`
          : `countryCode:${selectedCountryCode}`,
        lang: selectedLanguage,
        apiKey: env.HERE_REST_APIKEY,
      }

      return request(mountUrl('https://autocomplete.search.hereapi.com/v1/autocomplete', params), {
        contentType: false,
      }).then((res) => {
        return res.items ? res.items.filter((item) => Object.keys(item.address).includes('city')) : []
      })
    },

    reportProblem({ store, product, position, type, text }) {
      const body = {
        type,
        text,
        storeName: store.name,
        address: store.address,
        storeAddress: store.formattedAddress,
        productName: product.name,
        storeLocation: `${store.lat}, ${store.long}`,
        gtin: product.gtin,
        userLocation: `${position.lat},${position.lng}`,
        CNAE: store.CNAE,
        brand: store.brand,
        cnpj: store.cnpj,
        suppliers: store.suppliers,
        locatorExtraInfo: store.locatorExtraInfo,
      }

      return request(`${env.PREFIX}/report`, { method: 'POST', body })
    },

    ecommerce(gtin) {
      const params = {
        gtin,
      }

      return request(`${env.PREFIX}/ecommerce-list`, { method: 'GET', params })
    },

    ecommerceGoogle(gtin, offerId, location) {
      const params = {
        gtin,
        location,
      }

      if (offerId) params.offerId = offerId

      return request(`${env.PREFIX}/ecommerce-google-list`, { method: 'GET', params })
    },

    ecommerceHistoryInsights(gtin, period) {
      const params = {
        gtin,
        period,
      }

      return request(`${env.PREFIX}/ecommerce-google-history`, { method: 'GET', params })
    },

    onlineStore() {
      return request(`${env.PREFIX}/online-store`, { method: 'GET' })
    },

    postAlertPrice(params) {
      return request(`${env.PREFIX}/online-store-email-price-alarms`, {
        method: 'POST',
        body: params,
      })
    },

    postWhatsappData(params) {
      return request(`${env.PREFIX}/form-whatsapp`, { method: 'POST', params })
    },

    gmbStore(cnpj) {
      const params = { cnpj }
      return request(`${env.PREFIX}/gmb-details`, { method: 'GET', params })
    },

    saveLogger({ lat, lon, gtin, cnpj }) {
      const params = {
        lat,
        lon,
        gtin,
        cnpj,
      }

      return request(`${env.PREFIX}/save-logger`, { method: 'GET', params })
    },
  }
}

export default createApi()
